import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import type { Source } from "@onetasklist/shared";

/**
 * Resolves display strings for a live (non-backlog) source via the active i18n
 * locale. The key pattern mirrors the source id:
 *   board.connections.sources.{id}.displayName
 *   board.connections.sources.{id}.description
 *
 * Falls back to the API-provided value if the key is not found, so newly-added
 * sources degrade gracefully before translations are added.
 */
const resolveSource = (
  source: Source,
  t: (key: string, fallback: string) => string,
): Source => {
  if (source.backlog) return source;

  const prefix = `board.connections.sources.${source.id}`;
  return {
    ...source,
    displayName: t(`${prefix}.displayName`, source.displayName),
    description: t(`${prefix}.description`, source.description),
  };
};

export const useResolvedSources = (sources: readonly Source[]): Source[] => {
  const { t } = useTranslation();

  return useMemo(
    () => sources.map((s) => resolveSource(s, t)),
    // Re-resolve when language changes (t reference changes) or source list changes
    [sources, t],
  );
};
